import React, { useContext, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Progress from 'studio/components/Progress';
import useStyles from './ConfirmationModal.styles';

const ConfirmationModalContext = React.createContext(null);

const initialState = {
  open: false,
  title: '',
  content: null,
  onConfirm: null,
  confirmButtonText: 'Confirm',
  onClose: null,
  confirmDataTest: '',
  cancelDataTest: '',
  maxWidth: 'sm',
  fixedHeight: undefined,
  className: undefined
};

// This modal was created as a single global modal to be called in the whole application without having to add a new <Modal /> component
// each time we need a modal. (For now it is implemented only in Studio). By using this component, we can call an info or confirmation modal by just calling the "showConfirmationModal" function, and
// passing the inner information as an object with the parameters. By using it, we prevent too verbose code to call a modal, as well as avoid using a single state for each modal in a page.
//
// Parameters:
// The showConfirmationModal function receives only one parameter: an object that accepts predefined options. Here's an example:
//
// const parameterObject = {
//    title: '' (string);
//    content: '' (HTML element or string);
//    onConfirm: () => {} (function: if called, displays a `Confirm` that, when clicked, executes this function and closes the modal);
//    confirmButtonText: '' (string; changes the label of the `Confirm` button to a custom label);
//    onClose: () => {} (function: if called, executes an action before the modal is closed.);
//    confirmDataTest: '' (string: label to add to the data-test attribute in the Confirm button, to be used in E2E tests);
//    cancelDataTest: '' (string: label to add to the data-test attribute in the Cancel button, to be used in E2E tests);
//    maxWidth: 'sm' (string: the max width of the MUI modal, see maxWidth at https://mui.com/material-ui/api/dialog/);
//    fixedHeight: undefined (string: the height of the modal as CSS height property, if undefined, the modal will have a dynamic height);
//    className: undefined (string: the class name of the modal, if undefined, the modal will have no class name);
// }
//
// Usage example:
//
// 1. import the modal:
// import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
//
// 2. Activate the hooks function inside the root scope of the component:
// const { showConfirmationModal } = useConfirmationModal();
//
// 3. Use the modal as a function:
// It expects the parameterObject directly or a function as argument that gets passed functions to interact with the modal and that returns the parameterObject.
// - closeModal can be used e.g. for additional buttons in the modal content that should close the modal when clicked.
// showConfirmationModal(({ closeModal }) => ({ title: 'Title of the modal', content: `Content of the modal`, onConfirm: () => onConfirmFunction() }));
//
// OBS: If you want a simple info modal, call it with just `title` and `content`, without the `onConfirm` function. by omitting it, the `Confirm` button will not be displayed and the
// `Cancel` will be changed to `Close`.

// Just the confirmation modal itself, without any external state
// In contrast to using showConfirmationModal, this needs to be opened and closed manually
export const ConfirmationModal = props => {
  const {
    open,
    title = '',
    onConfirm = null,
    confirmButtonText = 'Confirm',
    onClose = null,
    confirmDataTest = '',
    cancelDataTest = '',
    disableConfirm = false,
    maxWidth = 'sm',
    fixedHeight = undefined,
    className = undefined,
    children
  } = props;

  const [loading, setLoading] = useState(false);

  const { classes } = useStyles();

  return (
    <Dialog
      maxWidth={maxWidth}
      fullWidth
      open={open}
      onClose={onClose}
      disabled={loading}
      data-test="confirmationModal"
      PaperProps={{
        style: { minHeight: fixedHeight, maxHeight: fixedHeight }
      }}
      className={className}
    >
      {title && <DialogTitle>{title}</DialogTitle>}
      <DialogContent>
        {loading ? (
          <div className={classes.progress}>
            <Progress />
          </div>
        ) : (
          children
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={async () => {
            if (onClose) {
              setLoading(true);
              await onClose();
              setLoading(false);
            }
          }}
          disabled={loading}
          data-test={cancelDataTest}
        >
          {onConfirm ? 'Cancel' : 'Close'}
        </Button>
        {onConfirm && (
          <Button
            autoFocus
            color="primary"
            onClick={async () => {
              setLoading(true);
              await onConfirm();
              setLoading(false);
            }}
            disabled={loading || disableConfirm}
            data-test={confirmDataTest}
          >
            {confirmButtonText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const ConfirmationModalProvider = ({ children }) => {
  const [modal, setModal] = useState(initialState);

  const closeModal = () => setModal(prev => ({ ...prev, open: false }));

  // function exported by HooksContext, where we open the modal and pass the parameters inside an object.
  // The parameters are optional, if you don't use any of them, these values below will be used as default.
  // I recommend always fill the `Title` and `Content`, otherwise they will show up as empty strings.
  const showConfirmationModal = propsOrBuilder => {
    const passedFunctions = { closeModal };

    const {
      title = '',
      content = '',
      onConfirm = null,
      confirmButtonText = 'Confirm',
      onClose = null,
      confirmDataTest = '',
      cancelDataTest = '',
      maxWidth = 'sm',
      fixedHeight = undefined,
      className = undefined
    } = typeof propsOrBuilder === 'function' ? propsOrBuilder(passedFunctions) : propsOrBuilder;

    setModal({
      open: true,
      title,
      content,
      onConfirm,
      confirmButtonText,
      onClose,
      confirmDataTest,
      cancelDataTest,
      maxWidth,
      fixedHeight,
      className
    });
  };

  // Closes the modal when clicking `Close/Cancel` button.
  // If any function is passed as `onClose` parameter in the `showConfirmationModal` function, it is called here before closing the modal.
  const onCloseModal = () => {
    modal.onClose?.();
    closeModal();
  };

  // Function that confirms the modal. The function passed as `onConfirm` in the `showConfirmationModal` function is called here, and then the modal is closed.
  // We can pass the onConfirm as a promise, in case of backend requests. If we do so, a `loading` spinner will be displayed replacing the `content`
  // and then the modal is closed.
  const onConfirmModal =
    modal.onConfirm &&
    (async () => {
      await modal.onConfirm();
      closeModal();
    });

  return (
    <ConfirmationModalContext.Provider value={{ showConfirmationModal }}>
      <ConfirmationModal
        open={modal.open}
        title={modal.title}
        onClose={onCloseModal}
        onConfirm={onConfirmModal}
        confirmButtonText={modal.confirmButtonText}
        confirmDataTest={modal.confirmDataTest}
        cancelDataTest={modal.cancelDataTest}
        maxWidth={modal.maxWidth}
        fixedHeight={modal.fixedHeight}
        className={modal.className}
      >
        {modal.content}
      </ConfirmationModal>
      {children}
    </ConfirmationModalContext.Provider>
  );
};

export const useConfirmationModal = () => {
  return useContext(ConfirmationModalContext);
};
